import { motion } from 'motion/react';
import { Check, X, Sparkles, Trophy } from 'lucide-react';

const competitors = [
  { name: 'Otras apps de tareas', hasAI: false, aiIncluded: false, web: false, family: false, latam: false, price: 'Gratis' },
  { name: 'Todoist / Notion', hasAI: true, aiIncluded: false, web: true, family: false, latam: false, price: '$10-20/mes' },
  { name: 'Apps con IA', hasAI: true, aiIncluded: false, web: true, family: false, latam: false, price: '$20+/mes' },
];

const frappiFeatures = [
  { name: 'IA integrada', value: true },
  { name: 'IA incluida en el precio', value: true },
  { name: 'Acceso web incluido (gratis)', value: true },
  { name: 'Estudiantes ilimitados', value: true },
  { name: 'Diseñado para LATAM', value: true },
  { name: 'Precio Premium', value: '$2.99/mes' },
];

function CheckBadge({ checked, strong = false }: { checked: boolean; strong?: boolean }) {
  return checked ? (
    <div className={`w-7 h-7 rounded-full flex items-center justify-center ${strong ? 'bg-emerald-100' : 'bg-emerald-50'}`}>
      <Check className={`w-4 h-4 ${strong ? 'text-emerald-600' : 'text-emerald-500/70'}`} />
    </div>
  ) : (
    <div className="w-7 h-7 rounded-full bg-red-50 flex items-center justify-center">
      <X className="w-4 h-4 text-red-400/60" />
    </div>
  );
}

export function Comparison() {
  return (
    <section className="py-24 md:py-36 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-muted/30 via-background to-muted/20" />
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/20 to-transparent" />

      <div className="container relative mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto text-center mb-16"
        >
          <motion.span
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-primary/20 text-primary text-sm font-semibold mb-6 shadow-sm"
          >
            Comparación
          </motion.span>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-extrabold text-foreground mb-6 tracking-tight">
            ¿Por qué Frappi es{' '}
            <span className="bg-gradient-to-r from-[#a67eec] via-[#5bb4f2] to-[#5ddba3] bg-clip-text text-transparent">
              diferente?
            </span>
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground">
            La única app que combina IA avanzada, acceso web y precio justo para LATAM.
          </p>
        </motion.div>

        {/* Comparison Table - Mobile */}
        <div className="md:hidden space-y-4 max-w-md mx-auto">
          {/* Frappi Card */}
          <motion.div
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-6 rounded-3xl bg-white/80 backdrop-blur-sm border-2 border-primary/25 shadow-lg shadow-primary/10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#a67eec] to-[#8b6de0] flex items-center justify-center shrink-0 shadow-lg">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-foreground text-lg">Frappi</h3>
                <p className="text-sm text-primary font-bold">$2.99/mes</p>
              </div>
            </div>
            <ul className="space-y-2.5">
              {frappiFeatures.slice(0, -1).map((feature) => (
                <li key={feature.name} className="flex items-center gap-2.5 text-sm">
                  <CheckBadge checked={true} strong />
                  <span className="text-foreground font-medium">{feature.name}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Competitors */}
          {competitors.map((comp, index) => (
            <motion.div
              key={comp.name}
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.3, delay: index * 0.06 }}
              className="p-6 rounded-3xl bg-white/50 backdrop-blur-sm border border-border/50"
            >
              <div className="flex items-center justify-between gap-2 mb-4">
                <h3 className="font-semibold text-foreground">{comp.name}</h3>
                <span className="text-sm text-muted-foreground font-medium whitespace-nowrap">{comp.price}</span>
              </div>
              <ul className="space-y-2.5">
                <li className="flex items-center gap-2.5 text-sm">
                  <CheckBadge checked={comp.hasAI} />
                  <span className="text-muted-foreground">IA integrada</span>
                </li>
                <li className="flex items-center gap-2.5 text-sm">
                  <CheckBadge checked={false} />
                  <span className="text-muted-foreground">IA incluida en precio</span>
                </li>
                <li className="flex items-center gap-2.5 text-sm">
                  <CheckBadge checked={comp.web} />
                  <span className="text-muted-foreground">Acceso web</span>
                </li>
              </ul>
            </motion.div>
          ))}
        </div>

        {/* Comparison Table - Desktop */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="hidden md:block max-w-5xl mx-auto"
        >
          <div className="rounded-3xl border border-border/50 bg-white/70 backdrop-blur-sm shadow-xl shadow-primary/5 mt-4">
            {/* Header Row */}
            <div className="grid grid-cols-5 border-b border-border/50">
              <div className="p-6 bg-muted/30">
                <span className="font-bold text-muted-foreground text-sm uppercase tracking-wider">Característica</span>
              </div>
              {/* Frappi Column */}
              <div className="p-6 pt-8 bg-gradient-to-b from-primary/8 to-primary/3 border-x-2 border-t-2 border-primary/20 rounded-t-2xl relative">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
                  <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-[#a67eec] to-[#8b6de0] text-white text-xs font-bold whitespace-nowrap shadow-xl shadow-primary/30 flex items-center gap-1">
                    <Sparkles size={10} />
                    Recomendado
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#a67eec] to-[#8b6de0] flex items-center justify-center shrink-0 shadow-lg">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <span className="font-bold text-foreground text-lg">Frappi</span>
                </div>
              </div>
              {competitors.map((comp) => (
                <div key={comp.name} className="p-6 bg-muted/20">
                  <span className="font-medium text-foreground text-sm">{comp.name}</span>
                </div>
              ))}
            </div>

            {/* Feature Rows */}
            {[
              { name: 'IA integrada', frappi: true, values: [false, true, true] },
              { name: 'IA incluida (sin costo extra)', frappi: true, values: [false, false, false], highlight: true },
              { name: 'Acceso web incluido (gratis)', frappi: true, values: [false, true, true] },
              { name: 'Estudiantes ilimitados (Premium)', frappi: true, values: [false, false, false] },
              { name: 'Diseñado para LATAM', frappi: true, values: [false, false, false] },
              { name: 'Funciona offline', frappi: true, values: [true, false, false] },
            ].map((row, index) => (
              <motion.div
                key={row.name}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.2, delay: index * 0.04 }}
                className={`grid grid-cols-5 border-b border-border/30 last:border-b-0 hover:bg-muted/10 transition-colors ${row.highlight ? 'bg-amber-50/50' : ''}`}
              >
                <div className="p-4 flex items-center">
                  <span className={`text-sm ${row.highlight ? 'font-bold text-foreground' : 'text-muted-foreground'}`}>
                    {row.name}
                    {row.highlight && <Sparkles className="inline-block w-3 h-3 ml-1 text-amber-500" />}
                  </span>
                </div>
                <div className={`p-4 flex items-center justify-center border-x-2 ${row.highlight ? 'border-primary/20 bg-primary/5' : 'border-primary/10'}`}>
                  <CheckBadge checked={true} strong />
                </div>
                {row.values.map((value, i) => (
                  <div key={i} className="p-4 flex items-center justify-center">
                    <CheckBadge checked={value} />
                  </div>
                ))}
              </motion.div>
            ))}

            {/* Price Row */}
            <div className="grid grid-cols-5 bg-muted/20">
              <div className="p-6 flex items-center">
                <span className="font-bold text-foreground">Precio mensual</span>
              </div>
              <div className="p-6 flex items-center justify-center border-x-2 border-b-2 border-primary/20 rounded-b-2xl bg-gradient-to-b from-primary/5 to-primary/10">
                <div className="text-center">
                  <span className="text-2xl font-extrabold bg-gradient-to-r from-[#a67eec] to-[#8b6de0] bg-clip-text text-transparent">$2.99</span>
                  <p className="text-xs text-muted-foreground whitespace-nowrap font-medium">IA + Web incluido</p>
                </div>
              </div>
              {competitors.map((comp, i) => (
                <div key={i} className="p-6 flex items-center justify-center">
                  <span className="text-lg font-semibold text-muted-foreground whitespace-nowrap">{comp.price}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="text-center mt-12"
        >
          <p className="text-muted-foreground text-lg">
            La mejor relación calidad-precio para estudiantes de LATAM.{' '}
            <a href="#pricing" className="text-primary font-bold hover:underline inline-flex items-center gap-1">
              Ver todos los planes
              <span className="inline-block group-hover:translate-x-0.5 transition-transform">&rarr;</span>
            </a>
          </p>
        </motion.div>
      </div>
    </section>
  );
}
